import { AxiosError } from 'axios'
import {
  useInfiniteQuery as useInfiniteQueryHook,
  UseInfiniteQueryOptions as UseInfiniteQueryBaseOptions,
  QueryKey,
} from '@tanstack/react-query'
import { useAuthentication } from '@/components/auth/AuthProvider'
import { useEffect, useRef } from 'react'

export type UseInfiniteQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<
  UseInfiniteQueryBaseOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryFnData,
    TQueryKey,
    string
  >,
  'initialPageParam'
>
export default function useInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  ...options
}: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const { getFreshIdToken } = useAuthentication()
  const hasRefreshed = useRef(false) // Used to prevent multiple refresh attempts

  const query = useInfiniteQueryHook<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    string
  >({
    ...options,
    initialPageParam: '',
  })

  useEffect(() => {
    if (
      !hasRefreshed.current &&
      (query.error as AxiosError)?.response?.status === 401
    ) {
      hasRefreshed.current = true
      getFreshIdToken()?.catch(() => {})
    }
  }, [getFreshIdToken, query.error])

  return query
}
